// Source of truth for gallery images = Supabase `gallery_images` table.
// Admin uploads + reorders + deletes mutate the DB so every device sees the
// same state. Legacy localStorage blobs are auto-migrated on first load.

const LEGACY_STORAGE_KEY = 'raztattoo_gallery_images';
const LEGACY_DELETED_IDS_KEY = 'raztattoo_deleted_ids';
const LEGACY_MIGRATED_FLAG = 'raztattoo_legacy_migrated_v1';

const CATEGORY_KEYS = [
    'kakoos',
    'peircings',
    'availablesketches',
    'covers',
    'hair',
];

const emptyCategories = () =>
    CATEGORY_KEYS.reduce((acc, key) => ({ ...acc, [key]: [] }), {});

function rowToImage(row) {
    return {
        id: row.public_id,
        public_id: row.public_id,
        src: row.src,
        alt: row.alt || row.category,
        createdAt: row.created_at ? new Date(row.created_at).getTime() : 0,
        position: row.position ?? 0,
    };
}

export async function getImages(supabase) {
    if (!supabase) return emptyCategories();
    const { data, error } = await supabase
        .from('gallery_images')
        .select('public_id, src, category, alt, position, created_at')
        .order('category', { ascending: true })
        .order('position', { ascending: true })
        .order('created_at', { ascending: false });

    if (error) {
        console.error('getImages failed', error);
        return emptyCategories();
    }

    const grouped = emptyCategories();
    for (const row of data || []) {
        if (!grouped[row.category]) grouped[row.category] = [];
        grouped[row.category].push(rowToImage(row));
    }
    return grouped;
}

export async function addImage(supabase, category, imageData) {
    if (!supabase) throw new Error('Supabase client not ready');

    const { data: maxRow } = await supabase
        .from('gallery_images')
        .select('position')
        .eq('category', category)
        .order('position', { ascending: false })
        .limit(1)
        .maybeSingle();

    const nextPosition = (maxRow?.position ?? -1) + 1;

    const { data, error } = await supabase
        .from('gallery_images')
        .insert({
            public_id: imageData.public_id,
            src: imageData.secure_url,
            category,
            alt: category,
            position: nextPosition,
        })
        .select()
        .single();

    if (error) {
        console.error('addImage failed', error);
        throw error;
    }

    return rowToImage(data);
}

export async function removeImage(supabase, category, public_id) {
    if (!supabase) throw new Error('Supabase client not ready');

    const { error } = await supabase
        .from('gallery_images')
        .delete()
        .eq('public_id', public_id)
        .eq('category', category);

    if (error) {
        console.error('removeImage failed', error);
        throw error;
    }

    return true;
}

export async function saveImages(supabase, imagesByCategory) {
    if (!supabase) throw new Error('Supabase client not ready');

    const updates = [];
    for (const [category, list] of Object.entries(imagesByCategory)) {
        (list || []).forEach((img, idx) => {
            updates.push({
                category,
                public_id: img.public_id,
                position: idx,
            });
        });
    }

    await Promise.all(
        updates.map((u) =>
            supabase
                .from('gallery_images')
                .update({ position: u.position })
                .eq('category', u.category)
                .eq('public_id', u.public_id),
        ),
    );
}

export async function reorderImages(supabase, category, newOrder) {
    if (!supabase) throw new Error('Supabase client not ready');

    await Promise.all(
        newOrder.map((img, idx) =>
            supabase
                .from('gallery_images')
                .update({ position: idx })
                .eq('category', category)
                .eq('public_id', img.public_id),
        ),
    );
}

// One-time migration: if legacy localStorage holds images and the DB is empty
// for that category, push them up so the client's laptop data survives the
// switch to Supabase.
async function migrateLegacyLocalStorage(supabase) {
    if (typeof window === 'undefined') return;
    if (localStorage.getItem(LEGACY_MIGRATED_FLAG) === '1') return;

    let legacy;
    try {
        const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
        legacy = raw ? JSON.parse(raw) : null;
    } catch {
        legacy = null;
    }

    if (!legacy) {
        localStorage.setItem(LEGACY_MIGRATED_FLAG, '1');
        return;
    }

    const rows = [];
    for (const category of CATEGORY_KEYS) {
        const list = legacy[category] || [];
        list.forEach((img, idx) => {
            if (!img?.public_id || !img?.src) return;
            rows.push({
                public_id: img.public_id,
                src: img.src,
                category,
                alt: img.alt || category,
                position: idx,
            });
        });
    }

    if (rows.length === 0) {
        localStorage.setItem(LEGACY_MIGRATED_FLAG, '1');
        return;
    }

    const { error } = await supabase
        .from('gallery_images')
        .upsert(rows, { onConflict: 'public_id', ignoreDuplicates: true });

    if (error) {
        console.error('Legacy migration failed', error);
        return;
    }

    localStorage.setItem(LEGACY_MIGRATED_FLAG, '1');
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    localStorage.removeItem(LEGACY_DELETED_IDS_KEY);
}

// Public entry point used by pages on mount. Runs legacy migration then
// returns the current DB state.
export async function loadMigratedImages(supabase) {
    if (!supabase) return emptyCategories();
    await migrateLegacyLocalStorage(supabase);
    return getImages(supabase);
}

export async function getImagesByCategory(supabase, category) {
    const all = await getImages(supabase);
    return all[category] || [];
}

export { CATEGORY_KEYS };
